import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Save, Send, Database, HardDrive, Trash2, CheckCircle, XCircle, AlertTriangle, Loader2, Stethoscope } from 'lucide-react';

const STEP_LABELS = {
  dns: 'Résolution DNS',
  connect: 'Connexion TCP',
  tls: 'Chiffrement TLS',
  auth: 'Authentification',
  send: 'Envoi',
};

const AdminSettingsPage = () => {
  const { language } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [smtpDiagnostic, setSmtpDiagnostic] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  // DB stats + maintenance
  const [dbStats, setDbStats] = useState(null);
  const [mediaStats, setMediaStats] = useState(null);
  const [cleaningInvites, setCleaningInvites] = useState(false);
  const [cleaningTokens, setCleaningTokens] = useState(false);
  const [confirmCleanup, setConfirmCleanup] = useState(null); // 'invites' | 'tokens' | null

  useEffect(() => {
    const load = async () => {
      try {
        const [settingsRes, dbRes, mediaRes] = await Promise.all([
          axios.get('/api/admin/settings'),
          axios.get('/api/admin/system/db-stats'),
          axios.get('/api/admin/media/stats'),
        ]);
        setSettings(settingsRes.data);
        setDbStats(dbRes.data);
        setMediaStats(mediaRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch('/api/admin/settings', settings);
      toast.success(t('success_saved', language));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) return;
    setTestingSMTP(true);
    setSmtpDiagnostic(null);
    try {
      const res = await axios.post('/api/admin/settings/test-email', { recipient: testEmail });
      toast.success(t('admin_smtp_test_success', language));
      if (res.data?.diagnostic) {
        setSmtpDiagnostic(res.data.diagnostic);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.diagnostic) {
        setSmtpDiagnostic(detail.diagnostic);
        toast.error(detail.message || t('admin_smtp_test_error', language));
      } else {
        toast.error(typeof detail === 'string' ? detail : t('admin_smtp_test_error', language));
      }
    } finally {
      setTestingSMTP(false);
    }
  };

  const handleSmtpDiagnose = async () => {
    setDiagnosing(true);
    setSmtpDiagnostic(null);
    try {
      const res = await axios.post('/api/admin/settings/smtp-diagnose');
      setSmtpDiagnostic(res.data);
      if (res.data.success) {
        toast.success('Diagnostic SMTP : tout est OK !');
      } else {
        toast.error(`Diagnostic SMTP : échec à l'étape "${STEP_LABELS[res.data.failed_step] || res.data.failed_step}"`);
      }
    } catch (err) {
      toast.error('Erreur lors du diagnostic SMTP');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleCleanInvites = async () => {
    setCleaningInvites(true);
    try {
      const res = await axios.post('/api/admin/maintenance/cleanup-invites');
      toast.success(`${t('admin_cleanup_success', language)} (${res.data.deleted})`);
    } catch (err) {
      toast.error(t('error_generic', language));
    } finally {
      setCleaningInvites(false);
    }
  };

  const handleCleanTokens = async () => {
    setCleaningTokens(true);
    try {
      const res = await axios.post('/api/admin/maintenance/cleanup-tokens');
      toast.success(`${t('admin_cleanup_success', language)} (${res.data.modified})`);
    } catch (err) {
      toast.error(t('error_generic', language));
    } finally {
      setCleaningTokens(false);
    }
  };

  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  if (loading || !settings) {
    return (
      <AdminLayout>
        <div className="animate-pulse text-muted-foreground">{t('loading', language)}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold font-cinzel">{t('admin_settings', language)}</h1>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {t('save', language)}
          </Button>
        </div>

        <Tabs defaultValue="general">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
            <TabsTrigger value="general">{t('admin_settings_general', language)}</TabsTrigger>
            <TabsTrigger value="smtp">{t('admin_settings_smtp', language)}</TabsTrigger>
            <TabsTrigger value="security">{t('admin_settings_security', language)}</TabsTrigger>
            <TabsTrigger value="announcements">{t('admin_settings_announcements', language)}</TabsTrigger>
            <TabsTrigger value="system">{t('admin_system', language)}</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('admin_site_name', language)}</Label>
                    <Input value={settings.site_name || ''} onChange={(e) => updateField('site_name', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_site_url', language)}</Label>
                    <Input value={settings.site_url || ''} onChange={(e) => updateField('site_url', e.target.value)} placeholder="https://..." />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin_site_description', language)}</Label>
                  <Textarea value={settings.site_description || ''} onChange={(e) => updateField('site_description', e.target.value)} rows={2} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('admin_logo_url', language)}</Label>
                    <Input value={settings.logo_url || ''} onChange={(e) => updateField('logo_url', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_favicon_url', language)}</Label>
                    <Input value={settings.favicon_url || ''} onChange={(e) => updateField('favicon_url', e.target.value)} />
                  </div>
                </div>
                <div className="rounded-md border border-primary/30 bg-primary/5 p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Switch
                      checked={settings.beta_mode_enabled === true}
                      onCheckedChange={(v) => updateField('beta_mode_enabled', v)}
                    />
                    <Label className="font-semibold">{language === 'fr' ? 'Mode beta fermée' : 'Closed beta mode'}</Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-12">
                    {language === 'fr'
                      ? "Quand activé, l'inscription directe est bloquée : un formulaire de demande d'accès apparaît sur la page d'accueil. Seuls les visiteurs ayant reçu une invitation avec un token unique peuvent créer leur compte."
                      : 'When enabled, direct registration is blocked: a waitlist form appears on the homepage. Only visitors with a unique invitation token can create an account.'}
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={settings.registration_enabled !== false}
                      onCheckedChange={(v) => updateField('registration_enabled', v)}
                    />
                    <Label>{t('admin_registration_enabled', language)}</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_max_users', language)}</Label>
                    <Input type="number" min={0} value={settings.max_users || 0} onChange={(e) => updateField('max_users', parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_max_campaigns', language)}</Label>
                    <Input type="number" min={0} value={settings.max_campaigns_per_user || 0} onChange={(e) => updateField('max_campaigns_per_user', parseInt(e.target.value) || 0)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin_default_language', language)}</Label>
                  <select
                    className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                    value={settings.default_language || 'fr'}
                    onChange={(e) => updateField('default_language', e.target.value)}
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMTP Settings */}
          <TabsContent value="smtp">
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Switch
                    checked={settings.email_enabled === true}
                    onCheckedChange={(v) => updateField('email_enabled', v)}
                  />
                  <Label className="font-semibold">{t('admin_email_enabled', language)}</Label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('admin_smtp_host', language)}</Label>
                    <Input value={settings.smtp_host || ''} onChange={(e) => updateField('smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_smtp_port', language)}</Label>
                    <Input type="number" value={settings.smtp_port || 587} onChange={(e) => updateField('smtp_port', parseInt(e.target.value) || 587)} />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('admin_smtp_user', language)}</Label>
                    <Input value={settings.smtp_user || ''} onChange={(e) => updateField('smtp_user', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_smtp_password', language)}</Label>
                    <Input type="password" value={settings.smtp_password || ''} onChange={(e) => updateField('smtp_password', e.target.value)} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={settings.smtp_use_tls !== false}
                    onCheckedChange={(v) => updateField('smtp_use_tls', v)}
                  />
                  <Label>{t('admin_smtp_tls', language)}</Label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('admin_smtp_from_email', language)}</Label>
                    <Input value={settings.smtp_from_email || ''} onChange={(e) => updateField('smtp_from_email', e.target.value)} placeholder="noreply@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_smtp_from_name', language)}</Label>
                    <Input value={settings.smtp_from_name || ''} onChange={(e) => updateField('smtp_from_name', e.target.value)} />
                  </div>
                </div>

                {/* SMTP Diagnostic & Test */}
                <div className="border-t pt-4 mt-4 space-y-4">
                  <h3 className="font-semibold">{t('admin_smtp_test', language)}</h3>

                  {/* Diagnose button */}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleSmtpDiagnose} disabled={diagnosing}>
                      {diagnosing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Stethoscope className="mr-2 h-4 w-4" />}
                      Diagnostiquer SMTP
                    </Button>
                  </div>

                  {/* Diagnostic results */}
                  {smtpDiagnostic && (
                    <div className="rounded-lg border border-border p-4 space-y-3 bg-muted/30">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        {smtpDiagnostic.success
                          ? <><CheckCircle className="h-5 w-5 text-green-500" /> <span className="text-green-500">Diagnostic réussi</span></>
                          : <><XCircle className="h-5 w-5 text-red-500" /> <span className="text-red-500">Diagnostic échoué</span></>
                        }
                      </div>

                      {/* Steps */}
                      <div className="space-y-2">
                        {smtpDiagnostic.steps?.map((step, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            {step.ok
                              ? <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                              : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                            }
                            <div>
                              <span className="font-medium">{STEP_LABELS[step.step] || step.step}</span>
                              <span className="text-muted-foreground ml-2">{step.detail}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Error + suggestion */}
                      {smtpDiagnostic.error_message && (
                        <div className="flex items-start gap-2 p-3 rounded bg-red-500/10 border border-red-500/20 text-sm">
                          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-medium text-red-500">{smtpDiagnostic.error_message}</p>
                            {smtpDiagnostic.suggestion && (
                              <p className="text-muted-foreground mt-1">{smtpDiagnostic.suggestion}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Test email */}
                  <div className="flex gap-2 max-w-md">
                    <Input
                      type="email"
                      placeholder={t('email', language)}
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                    <Button onClick={handleTestEmail} disabled={testingSMTP || !testEmail}>
                      {testingSMTP ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      {t('admin_test_btn', language)}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security">
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('admin_jwt_expiration', language)}</Label>
                    <Input type="number" min={1} max={720} value={settings.jwt_expiration_hours || 24} onChange={(e) => updateField('jwt_expiration_hours', parseInt(e.target.value) || 24)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('admin_password_min_length', language)}</Label>
                    <Input type="number" min={4} max={128} value={settings.password_min_length || 6} onChange={(e) => updateField('password_min_length', parseInt(e.target.value) || 6)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('admin_cors_origins', language)}</Label>
                  <Textarea
                    value={settings.cors_origins_custom || ''}
                    onChange={(e) => updateField('cors_origins_custom', e.target.value)}
                    placeholder="https://example.com, https://other.com"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements */}
          <TabsContent value="announcements">
            <Card>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-2">
                  <Label>{t('admin_announcement', language)}</Label>
                  <p className="text-xs text-muted-foreground">{t('admin_announcement_help', language)}</p>
                  <Textarea
                    value={settings.announcement || ''}
                    onChange={(e) => updateField('announcement', e.target.value)}
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System & Maintenance */}
          <TabsContent value="system">
            <div className="space-y-4">
              {/* DB Stats */}
              {dbStats && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Database className="h-5 w-5" />
                      {t('admin_db_status', language)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">{t('admin_db_name', language)}</p>
                        <p className="font-medium">{dbStats.db_name}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{t('admin_data_size', language)}</p>
                        <p className="font-medium">{formatBytes(dbStats.data_size)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{t('admin_storage_size', language)}</p>
                        <p className="font-medium">{formatBytes(dbStats.storage_size)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Index</p>
                        <p className="font-medium">{formatBytes(dbStats.index_size)}</p>
                      </div>
                    </div>
                    {dbStats.collections && (
                      <div className="mt-4 border-t pt-4">
                        <h4 className="font-medium mb-2">{t('admin_collections_label', language)}</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                          {Object.entries(dbStats.collections).map(([name, stats]) => (
                            <div key={name} className="flex justify-between p-2 rounded bg-muted/50">
                              <span className="text-muted-foreground">{name}</span>
                              <span className="font-medium">{stats.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Media Stats */}
              {mediaStats && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <HardDrive className="h-5 w-5" />
                      {t('admin_media_label', language)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">{t('admin_songs_count', language)}</p>
                        <p className="font-medium">{mediaStats.songs_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">{t('admin_total_size', language)}</p>
                        <p className="font-medium">{formatBytes(mediaStats.total_size)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Maintenance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Trash2 className="h-5 w-5" />
                    {t('admin_maintenance', language)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" onClick={() => setConfirmCleanup('invites')} disabled={cleaningInvites}>
                    {t('admin_cleanup_invites', language)}
                  </Button>
                  <Button variant="outline" className="ml-3" onClick={() => setConfirmCleanup('tokens')} disabled={cleaningTokens}>
                    {t('admin_cleanup_tokens', language)}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        <Dialog open={!!confirmCleanup} onOpenChange={() => setConfirmCleanup(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin_maintenance_confirm_title', language)}</DialogTitle>
              <DialogDescription>{t('admin_maintenance_confirm_desc', language)}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmCleanup(null)}>{t('cancel', language)}</Button>
              <Button onClick={() => {
                if (confirmCleanup === 'invites') handleCleanInvites();
                else handleCleanTokens();
                setConfirmCleanup(null);
              }}>{t('confirm', language)}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
